import { Component, inject, PLATFORM_ID, signal, OnInit, LOCALE_ID } from '@angular/core';
import { FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../../../../services/auth-service';
import { LoginUserDTO, LoginUserVTO, ErrorVTO } from '../../../../services/auth-service.model';
import { MobileInput } from '../register-component/mobile-input/mobile-input';
import { CurrentUserService } from '../../../../services/current-user.service';
import { CustomValidators } from '../../../../validators/custom-validators';
import { ScopeService } from '../../../../services/scope/scope-service';
import { Error } from '../shared/error/error';
import { ImageSide } from '../shared/image-side/image-side';
import { UnauthorizedService } from '../../../../services/unauthorized.service';

@Component({
  selector: 'app-login-component',
  imports: [CommonModule, RouterModule, ReactiveFormsModule, MobileInput, RouterLink, Error, ImageSide],
  templateUrl: './login-component.html',
  styleUrl: './login-component.scss',
})
export class LoginComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private currentUserService = inject(CurrentUserService);
  private scopeService = inject(ScopeService);
  private locale = inject(LOCALE_ID);
  private unauthorizedService = inject(UnauthorizedService);

  currentLocale = signal<'en' | 'ar'>('en');

  apiError = signal('');
  showPassword: boolean = false;
  showVerifyLink = signal(false);
  loading = signal(false);

  loginForm = new FormGroup({
    mobileNumber: new FormControl('', {
      validators: [CustomValidators.mobileNumber()],
    }),
    password: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(50),
        CustomValidators.strongPassword(),
      ],
    }),
  });

  ngOnInit() {
    this.currentLocale.set(this.locale.startsWith('ar') ? 'ar' : this.locale.startsWith('en') ? 'en' : 'en');
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('registeredMobile');
      localStorage.removeItem('forgotUsername');
      localStorage.removeItem('otpMethod');
      const saved = localStorage.getItem('loginForm');
      if (saved) {
        const values = JSON.parse(saved);
        this.loginForm.patchValue(values);
      }
      this.loginForm.valueChanges.subscribe((value) => {
        const { password, ...toSave } = value;
        localStorage.setItem('loginForm', JSON.stringify(toSave));
      });
    }
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    this.apiError.set('');
    this.showVerifyLink.set(false);
    this.loading.set(true);

    const mobileControlValue = this.loginForm.value.mobileNumber as { dialCode: string; number: string } | null;
    let fullMobileNumber = '';
    if (mobileControlValue?.number.startsWith('0') && mobileControlValue?.number.length === 11) {
      fullMobileNumber = mobileControlValue.dialCode + mobileControlValue.number.slice(1);
    }
    if (!mobileControlValue?.number.startsWith('0') && mobileControlValue?.number.length === 10) {
      fullMobileNumber = mobileControlValue.dialCode + mobileControlValue.number;
    }

    // const fullMobileNumber = (mobileControlValue?.dialCode || '') + (mobileControlValue?.number || '');
    const payload: LoginUserDTO = {
      username: fullMobileNumber,
      password: this.loginForm.get('password')?.value || '',
    };

    // const mobileControlValue = this.loginForm.value.phoneNumber as { dialCode: string; number: string } | null;
    // const fullMobileNumber = (mobileControlValue?.dialCode || '') + (mobileControlValue?.number || '');

    // const payload: LoginUserDTO = {
    //   username: fullMobileNumber,
    //   password: this.loginForm.get('password')?.value || '',
    // };

    this.authService
      .loginUser(payload)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          if (isPlatformBrowser(this.platformId)) {
            const token = (res as LoginUserVTO).accessToken;
            const expires = new Date();
            expires.setDate(expires.getDate() + 7);

            document.cookie = `accessToken=${token}; expires=${expires.toUTCString()}; path=/; samesite=strict`;
            localStorage.removeItem('loginForm');

            this.scopeService.loadUserContext().subscribe({
              next: () => {
                this.currentUserService.setLoggedIn(true);
                this.unauthorizedService.clear();

                const userScopes = this.scopeService.getUserScopes();
                const isCustomer = userScopes.includes(510007);

                console.log(' Login successful');
                console.log(' User Scopes count:', userScopes.length);
                console.log('Is Customer?', isCustomer);

                if (isCustomer) {
                  this.router.navigate(['/user']);
                } else {
                  this.router.navigate(['/admin']);
                }
              },
              error: (err: any) => {
                console.error(' Context Error:', err);
                this.apiError.set('Failed to load user permissions.');
              },
            });
          }
        },
        error: (err: ErrorVTO) => {
          this.loading.set(false);
          this.apiError.set(this.isArabic() ? err.messageAr || 'حدث بعض الخطأ' : err.messageEn || 'Login failed');
        },
      });
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.loginForm.get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

  hasError(fieldName: string, errorName: string): boolean {
    return !!(this.loginForm.get(fieldName)?.hasError(errorName) && this.loginForm.get(fieldName)?.touched);
  }

  onVerifyAccount() {
    const mobileControlValue = this.loginForm.value.mobileNumber as { dialCode: string; number: string } | null;
    if (mobileControlValue && isPlatformBrowser(this.platformId)) {
      const fullMobileNumber = (mobileControlValue?.dialCode || '') + (mobileControlValue?.number || '');
      localStorage.setItem('registeredMobile', fullMobileNumber);
      localStorage.setItem('otpMethod', 'SMS');
      sessionStorage.setItem('fromLogin', 'true');
      this.router.navigate(['/register/otp-verify']);
    }
  }
  private errorCodeToMessage(code: string): string {
    if (code == '0001') {
      return $localize`:@@loginErrorNotRegistered:This phone number is not registered.`;
    } else if (code == '0002') {
      return $localize`:@@loginErrorIncorrectPassword:The password you entered is incorrect.`;
    } else {
      return $localize`:@@loginErrorUnexpected:An unexpected error occurred. Please try again later.`;
    }
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }

  isEnglish() {
    return this.locale.startsWith('en');
  }
}
